// =============================================================================
// PAGES/ESTEIRAEDITOR.TSX — Criar/Editar esteira (CRM é dono; estilo Ploomes)
// =============================================================================
// /esteiras/novo (criar) e /esteiras/:id/editar. Informações (nome/cor/ícone) +
// Etapas (criar/editar/reordenar/remover) + "Configurar checklist" por etapa
// (grupos → itens, via ChecklistGrupos). Substitui as telas admin avulsas de
// Etapas e Checklist. Admin-only (rota protegida no App).
// =============================================================================
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Loader2, ChevronDown, ChevronRight, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { pipelinesApi, type PipelineDetalhe, type PipelineEstagioDetalhe } from '../services/api';
import ChecklistGrupos from '../components/ChecklistGrupos';
import ConfirmModal from '../components/ConfirmModal';

export default function EsteiraEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const criando = !id;

  const [novoNome, setNovoNome] = useState('');
  const [salvandoNova, setSalvandoNova] = useState(false);
  const [pipe, setPipe] = useState<PipelineDetalhe | null>(null);
  const [carregando, setCarregando] = useState(!criando);
  const [novaEtapa, setNovaEtapa] = useState('');
  const [etapaAberta, setEtapaAberta] = useState<string | null>(null);
  const [confirmRemoverEtapa, setConfirmRemoverEtapa] = useState<PipelineEstagioDetalhe | null>(null);
  const [removendoEtapa, setRemovendoEtapa] = useState(false);

  const carregar = useCallback(() => {
    if (!id) return;
    setCarregando(true);
    pipelinesApi.pipelineDetalhe(id).then(setPipe).catch(() => toast.error('Falha ao carregar a esteira.')).finally(() => setCarregando(false));
  }, [id]);
  useEffect(() => { carregar(); }, [carregar]);

  // ── Criar ──
  const criar = async () => {
    if (!novoNome.trim()) return;
    setSalvandoNova(true);
    try { const r = await pipelinesApi.criarPipeline({ nome: novoNome.trim() }); navigate(`/esteiras/${r.id}/editar`); }
    catch { toast.error('Falha ao criar esteira.'); setSalvandoNova(false); }
  };

  // ── Informações ──
  const patchPipe = async (data: { nome?: string; cor?: string | null; icone?: string | null }) => {
    if (!pipe) return;
    setPipe({ ...pipe, ...data });
    try { await pipelinesApi.atualizarPipeline(pipe.id, data); } catch { toast.error('Não foi possível salvar.'); carregar(); }
  };

  // ── Etapas ──
  const addEtapa = async () => {
    if (!pipe || !novaEtapa.trim()) return;
    try { const e = await pipelinesApi.criarEstagio({ pipelineId: pipe.id, nome: novaEtapa.trim() }); setPipe({ ...pipe, estagios: [...pipe.estagios, e] }); setNovaEtapa(''); }
    catch { toast.error('Falha ao criar etapa.'); }
  };
  const patchEtapa = async (eid: string, data: { nome?: string; ultimo?: boolean; ativo?: boolean }) => {
    if (!pipe) return;
    setPipe({ ...pipe, estagios: pipe.estagios.map((e) => e.id === eid ? { ...e, ...data } : e) });
    try { await pipelinesApi.atualizarEstagio(eid, data); } catch { toast.error('Não foi possível salvar a etapa.'); carregar(); }
  };
  const confirmarRemoverEtapa = async () => {
    if (!pipe || !confirmRemoverEtapa) return;
    const eid = confirmRemoverEtapa.id;
    setRemovendoEtapa(true);
    try {
      await pipelinesApi.excluirEstagio(eid);
      setPipe({ ...pipe, estagios: pipe.estagios.filter((e) => e.id !== eid) });
      setConfirmRemoverEtapa(null);
    } catch { toast.error('Falha ao remover etapa.'); } finally { setRemovendoEtapa(false); }
  };
  const mover = async (idx: number, dir: -1 | 1) => {
    if (!pipe) return;
    const arr = [...pipe.estagios]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setPipe({ ...pipe, estagios: arr });
    try { await pipelinesApi.reordenarEstagios(pipe.id, arr.map((e) => e.id)); } catch { toast.error('Falha ao reordenar.'); carregar(); }
  };

  // ── Render ──
  if (criando) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1 text-sm text-otm-txt-3 hover:text-otm-txt-1 mb-4"><ArrowLeft size={16} /> Voltar</button>
        <h1 className="text-xl font-bold text-otm-txt-1 mb-1">Nova esteira</h1>
        <p className="text-sm text-otm-txt-3 mb-4">Dê um nome para a esteira. Depois você adiciona as etapas e os checklists.</p>
        <div className="flex gap-2">
          <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') criar(); }} placeholder="Nome da esteira…" className="flex-1 text-sm border border-otm-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-otm-navy/20" />
          <button onClick={criar} disabled={!novoNome.trim() || salvandoNova} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-otm-navy px-4 py-2 rounded-lg disabled:opacity-40">{salvandoNova ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Criar</button>
        </div>
      </div>
    );
  }

  if (carregando || !pipe) return <div className="flex items-center gap-2 text-otm-txt-3 py-10 justify-center"><Loader2 size={18} className="animate-spin" /> Carregando…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <button onClick={() => navigate('/')} className="inline-flex items-center gap-1 text-sm text-otm-txt-3 hover:text-otm-txt-1 mb-4"><ArrowLeft size={16} /> Voltar para Operações</button>

      {/* Informações */}
      <section className="bg-white rounded-xl border border-otm-border p-4 mb-4">
        <h2 className="text-sm font-bold text-otm-txt-1 mb-3">Informações da esteira</h2>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_1fr] gap-3">
          <label className="block"><span className="text-[12px] text-otm-txt-3">Nome</span>
            <input defaultValue={pipe.nome} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== pipe.nome) patchPipe({ nome: e.target.value.trim() }); }} className="w-full text-sm border border-otm-border rounded-lg px-3 py-1.5 mt-0.5 focus:outline-none focus:ring-2 focus:ring-otm-navy/20" />
          </label>
          <label className="block"><span className="text-[12px] text-otm-txt-3">Cor</span>
            <input type="color" defaultValue={pipe.cor ?? '#44ea18'} onBlur={(e) => patchPipe({ cor: e.target.value })} className="w-full h-9 border border-otm-border rounded-lg mt-0.5" />
          </label>
          <label className="block"><span className="text-[12px] text-otm-txt-3">Ícone (emoji/nome)</span>
            <input defaultValue={pipe.icone ?? ''} onBlur={(e) => { if (e.target.value !== (pipe.icone ?? '')) patchPipe({ icone: e.target.value || null }); }} placeholder="ex.: 💰" className="w-full text-sm border border-otm-border rounded-lg px-3 py-1.5 mt-0.5 focus:outline-none focus:ring-2 focus:ring-otm-navy/20" />
          </label>
        </div>
      </section>

      {/* Etapas */}
      <section className="bg-white rounded-xl border border-otm-border p-4">
        <h2 className="text-sm font-bold text-otm-txt-1 mb-3">Etapas</h2>
        <div className="space-y-2">
          {pipe.estagios.map((e, idx) => (
            <EtapaRow key={e.id} etapa={e} idx={idx} total={pipe.estagios.length} aberta={etapaAberta === e.id}
              onToggle={() => setEtapaAberta(etapaAberta === e.id ? null : e.id)}
              onPatch={patchEtapa} onRemove={setConfirmRemoverEtapa} onMover={mover} />
          ))}
          {pipe.estagios.length === 0 && <p className="text-[13px] text-otm-txt-3">Nenhuma etapa ainda.</p>}
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-otm-border">
          <input value={novaEtapa} onChange={(e) => setNovaEtapa(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addEtapa(); }} placeholder="Nova etapa…" className="flex-1 text-sm border border-otm-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-otm-navy/20" />
          <button onClick={addEtapa} disabled={!novaEtapa.trim()} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-otm-navy px-3 py-1.5 rounded-lg disabled:opacity-40"><Plus size={14} /> Etapa</button>
        </div>
      </section>

      <ConfirmModal
        aberto={!!confirmRemoverEtapa}
        titulo="Remover etapa"
        tom="perigo"
        processando={removendoEtapa}
        textoConfirmar="Remover"
        mensagem={<>Remover a etapa <strong>{confirmRemoverEtapa?.nome}</strong> e todos os checklists dela? Esta ação é definitiva.</>}
        onConfirmar={confirmarRemoverEtapa}
        onCancelar={() => { if (!removendoEtapa) setConfirmRemoverEtapa(null); }}
      />
    </div>
  );
}

function EtapaRow({ etapa, idx, total, aberta, onToggle, onPatch, onRemove, onMover }: {
  etapa: PipelineEstagioDetalhe; idx: number; total: number; aberta: boolean;
  onToggle: () => void;
  onPatch: (id: string, d: { nome?: string; ultimo?: boolean; ativo?: boolean }) => void;
  onRemove: (etapa: PipelineEstagioDetalhe) => void;
  onMover: (idx: number, dir: -1 | 1) => void;
}) {
  return (
    <div className={`rounded-lg border ${etapa.ativo ? 'border-otm-border' : 'border-dashed border-otm-border bg-otm-subtle/40'}`}>
      <div className="flex flex-wrap items-center gap-2 p-2.5">
        <div className="flex flex-col">
          <button onClick={() => onMover(idx, -1)} disabled={idx === 0} className="text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-30 leading-none text-xs">▲</button>
          <button onClick={() => onMover(idx, 1)} disabled={idx === total - 1} className="text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-30 leading-none text-xs">▼</button>
        </div>
        <input defaultValue={etapa.nome} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== etapa.nome) onPatch(etapa.id, { nome: e.target.value.trim() }); }} className="flex-1 min-w-[160px] text-[13px] font-medium text-otm-txt-1 bg-transparent border-b border-transparent focus:border-otm-border focus:outline-none py-0.5" />
        <label className="text-[12px] text-otm-txt-2 inline-flex items-center gap-1" title="Etapa de ganho/handoff"><input type="checkbox" checked={etapa.ultimo} onChange={(e) => onPatch(etapa.id, { ultimo: e.target.checked })} /> última</label>
        <label className="text-[12px] text-otm-txt-2 inline-flex items-center gap-1"><input type="checkbox" checked={etapa.ativo} onChange={(e) => onPatch(etapa.id, { ativo: e.target.checked })} /> ativa</label>
        <button onClick={onToggle} className="inline-flex items-center gap-1 text-[12px] text-otm-navy font-medium px-2 py-1 rounded hover:bg-otm-navy/5">
          {aberta ? <ChevronDown size={14} /> : <ChevronRight size={14} />} <ListChecks size={13} /> checklist
        </button>
        <button onClick={() => onRemove(etapa)} title="Remover etapa" className="text-otm-txt-3 hover:text-red-600"><Trash2 size={15} /></button>
      </div>
      {aberta && <div className="px-3 pb-3 pt-1 border-t border-otm-subtle bg-otm-subtle/20"><ChecklistGrupos estagioId={etapa.id} /></div>}
    </div>
  );
}
